// modules
import {
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  Text,
  View,
  Button,
} from '@upstox/upstox-ui';
import React, { FC, ReactNode, useCallback, useEffect, useState } from 'react';
import { observer } from 'mobx-react';
// modules
import LocaleService from 'modules/Localization';
import { useStore } from 'modules/Stores';
import Format from 'modules/Format';
import Search from 'modules/Search';
// typings
import {
  AlertStatusFilter,
  GetPriceAlertObject,
} from 'modules/PriceAlerts/typings';
// components
import WithTooltip from 'modules/WithTooltip/components/WithTooltip';
import PriceAlertsEmptyState from 'modules/PriceAlerts/components/PriceAlertsEmptyState';
import PriceAlertsLoadingBody from 'modules/PriceAlerts/components/PriceAlertsLoadingState/PriceAlertsLoadingBody';
import PriceAlertsErrorState from 'modules/PriceAlerts/components/PriceAlertsErrorState';
// assets
import NoteLogo from 'assets/note-logo.svg';
import EditLogo from 'assets/edit-logo.svg';
import DeleteLogo from 'assets/delete-logo.svg';
import ChartLogo from 'assets/chart-logo.svg';
// typings
import { SEGMENT_TO_DISPLAY_NAME_MAP } from 'modules/Ordering/consts';
// styles
import * as css from './styles';
// constants
import { ALERT_TABLE_HEADINGS, ALERT_TABLE_HEADINGS_KEYS } from './constant';

const PriceAlertListBody: FC = () => {
  const {
    priceAlertList,
    selectedStatus,
    setToDeleteAlertId,
    setCurrentAlertId,
    setPriceAboveNotes,
    setPriceBelowNotes,
    setPriceAbove,
    setPriceBelow,
    setModalType,
    setScripData,
  } = useStore(
    'PriceAlertsStore',
  );
  const { setChartSymbol } = useStore('TradingChartStore');
  const { toggleModal } = useStore('AppStore');

  const { getLoading } = useStore('LoadingStore');
  const { getError } = useStore('ErrorStore');
  const isLoading = getLoading('PRICE_ALERT');
  const isError = getError('PRICE_ALERT');

  const [filteredPriceAlertList, setFilteredPriceAlertList] = useState<
    GetPriceAlertObject[]
  >([]);

  useEffect(() => {
    if (selectedStatus === AlertStatusFilter.ALL) {
      setFilteredPriceAlertList(priceAlertList);
    } else {
      const filteredList = priceAlertList.filter(alert =>
        alert.rules.some(rule => rule.status === selectedStatus),
      );
      setFilteredPriceAlertList(filteredList);
    }
  }, [priceAlertList, selectedStatus]);

  const onDelete = useCallback((id: string) => {
      setToDeleteAlertId(id);
      setModalType('Delete');
    }, [setToDeleteAlertId, setModalType]);

  const onEdit = useCallback(async (alert: GetPriceAlertObject) => {
    const exchangeToken = `${alert.instrument.e}|${alert.instrument.t}`;
    const scripData = await Search.searchByExchangeToken(exchangeToken);
    setScripData(scripData);
    setCurrentAlertId(alert.id);
    const priceBelow = alert.rules.find(rule => rule.type === 'BELOW');
    const priceAbove = alert.rules.find(rule => rule.type === 'ABOVE');
    if (priceBelow) {
      setPriceBelowNotes(priceBelow.notes);
      setPriceBelow(priceBelow.value.toString());
    }
    if (priceAbove) {
      setPriceAbove(priceAbove.value.toString());
      setPriceAboveNotes(priceAbove.notes);
    }
    setModalType('Create');
  },
  [
    setScripData,
    setCurrentAlertId,
    setPriceAbove,
    setPriceBelow,
    setModalType,
    setPriceAboveNotes,
    setPriceBelowNotes,
  ]);

  const showChart = useCallback((alertDetail: GetPriceAlertObject) => {
    const exchangeToken = `${alertDetail.instrument.e}|${alertDetail.instrument.t}`;
    setChartSymbol(exchangeToken);
    setModalType('List');
    toggleModal('priceAlerts');
  }, [setChartSymbol, setModalType, toggleModal]);

  const listTableCellMapper = useCallback(
    (
      key: ALERT_TABLE_HEADINGS_KEYS,
      alertDetail: GetPriceAlertObject,
    ) => {
      const { rules } = alertDetail;
      const notes = rules.map(item => item.notes).filter(note => note).join(', ');

      const cellContent: Record<
        ALERT_TABLE_HEADINGS_KEYS,
        ReactNode
        > = {
        scripName: (
          <TableCell width={ALERT_TABLE_HEADINGS[key].width} key={`${key}-${alertDetail.id}`} padding="xsmall" align="left">
            <View css={css.ScripNameContainer}>
              <Text css={css.TableCell}>
                {alertDetail.instrument.s || alertDetail.instrument.t}
              </Text>
              <Text css={css.SegmentName}>
                {SEGMENT_TO_DISPLAY_NAME_MAP[alertDetail.instrument.e]}
              </Text>
              {notes
              ? (
                <WithTooltip
                  id="note-tooltip"
                  data-id="note-tooltip"
                  trigger="hover"
                  tooltip={notes}
                  position="TopRight"
                >
                  <img alt="Note Logo" src={NoteLogo} draggable={false} />
                </WithTooltip>
              ) : null}
            </View>
          </TableCell>
        ),
        alertPrice: (
          <TableCell width={ALERT_TABLE_HEADINGS[key].width} key={`${key}-${alertDetail.id}`} padding="xsmall" align="right">
            <View css={css.AlertPriceContainer}>
              {rules.map(({ value, type }) => (
                <Text
                  key={`${key}-${alertDetail.id}-${type}-price`}
                  css={css.AlertPrice}
                >
                  {type === 'ABOVE' ? LocaleService.translate('ABOVE_CAPITALIZE') : LocaleService.translate('BELOW_CAPITALIZE')}: {Format.formatToIndianFloat(value)}
                </Text>
              ))}
            </View>
          </TableCell>
        ),
        status: (
          <TableCell width={ALERT_TABLE_HEADINGS[key].width} key={`${key}-${alertDetail.id}`} padding="xsmall" align="right">
            <View css={css.AlertPriceContainer}>
              {rules.map(({ status, type }) => (
                <Text
                  key={`${key}-${alertDetail.id}-${type}-status`}
                  family="tvFont"
                  size="m"
                  color={status === 'ACTIVE' ? 'text.positive' : 'background.yellow'}
                >
                  {status === 'ACTIVE' ? LocaleService.translate('ACTIVE') : LocaleService.translate('TRIGGERED')}
                </Text>
              ))}
            </View>
          </TableCell>
        ),
        action: (
          <TableCell width={ALERT_TABLE_HEADINGS[key].width} key={`${key}-${alertDetail.id}`} padding="xsmall" align="right">
            <View css={css.ActionContainer}>
              <Button onClick={() => showChart(alertDetail)} css={css.ActionButton} variant="white">
                <img src={ChartLogo} alt="goToChart" />
              </Button>
              {rules.find(rule => rule.status === 'ACTIVE') && (
                <Button onClick={() => onEdit(alertDetail)} css={css.ActionButton} variant="white">
                  <img src={EditLogo} alt="edit" />
                </Button>
              )}
              <Button onClick={() => onDelete(alertDetail.id)} css={css.ActionButton} variant="white">
                <img src={DeleteLogo} alt="delete" />
              </Button>
            </View>
          </TableCell>
        ),
      };
      return cellContent[key] || null;
    },
    [onDelete, onEdit, showChart],
  );

  if (isLoading) {
    return <PriceAlertsLoadingBody />;
  }
  if (isError) {
    return <PriceAlertsErrorState />;
  }
  if (filteredPriceAlertList?.length === 0) return <PriceAlertsEmptyState />;

  return (
    <View css={css.TableContainer}>
      <Table css={css.Table}>
        <TableHead>
          <TableRow css={css.TableRow}>
            {Object
              .values(ALERT_TABLE_HEADINGS)
              .map(({ key, heading, align, width }) => (
                <TableCell width={width} padding="xsmall" key={key} align={align}>
                  <Text css={css.HeaderCell}>
                    {heading && LocaleService.translate(heading)}
                  </Text>
                </TableCell>
              ))}
          </TableRow>
        </TableHead>
        <TableBody css={css.TableBody}>
          {filteredPriceAlertList.map(item => (
            <TableRow key={item.id} css={css.TableRow}>
              {Object.keys(ALERT_TABLE_HEADINGS).map(key =>
                listTableCellMapper(key, item),
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </View>
  );
};

export default observer(PriceAlertListBody);
